import { commonLaboratoryService } from '@/api/resources/common/laboratory/common-laboratory.service'
import { ConfigurationLocalAccountingRatesCopyModalState, initialLocalAccountingRatesCopyModalState } from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-grid-copy-modal/configuration-local-accounting-billable-rate-grid-copy-modal.model'
import { BillableRateCopyRequest, BillableRateResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/configuration-local-accounting-billable-rates/billable-rate.dto'
import { billableRateService } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/configuration-local-accounting-billable-rates/billable-rate.service'
import { StrictNullable } from '@/shared/types/app.types'
import { BModal } from 'bootstrap-vue'
import { Vue, Component, Ref, Emit } from 'vue-property-decorator'
import { DateUtils } from '@/shared/util/date-utils'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import {
    configurationAccountingBillableUnitsService
} from '@/modules/configurationModule/detail/accounting/billable/units/services/configuration-accounting-billable-unit/configuration-accounting-billable-unit.service'
import { AccountingUnitResponse } from '@/api/services/billing/common-accounting-unit/billing-common-accounting-unit.dto'

@Component
export default class ConfigurationLocalAccountingBillableRateGridCopyModal extends Vue {
    @Ref() readonly ratesCopyModal!: BModal

    @Emit()
    create(): void {}

    state: ConfigurationLocalAccountingRatesCopyModalState
    constructor() {
        super()
        this.state = initialLocalAccountingRatesCopyModalState()
    }

    get modelValidated(): boolean {
        const nameExists = !!this.state.copyRate.name.length
        const codeExists = !!this.state.copyRate.code.length
        const laboratoryExists = !!this.state.copyRate.laboratory
        const startDateExists = !!this.state.copyRate.startDate
        const endDateExists = !!this.state.copyRate.endDate
        const hasErrors: boolean = this.$validator.errors.any() || !nameExists || !codeExists || !laboratoryExists || !startDateExists || !endDateExists
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    get dateToday(): Date {
        return new Date()
    }

    get dateStartCanUpdate(): boolean {
        if (this.state.copyRate?.startDate) {
            return this.state.copyRate?.startDate.setHours(0, 0, 0, 0) >= this.state.todayDate.setHours(0, 0, 0, 0)
        }
        return false
    }

    get beforeDateStartDate(): Date {
        return this.state.todayDate
    }

    get afterDateStartDate(): StrictNullable<Date> {
        if (!this.state.copyRate.endDate) {
            return null
        }
        return this.state.copyRate.endDate || this.state.finishDate
    }

    get beforeDateEndDate(): Date {
        if (this.state.copyRate?.startDate) {
            return this.state.copyRate.startDate.setHours(0, 0, 0, 0) > this.state.todayDate.setHours(0, 0, 0, 0)
                ? this.state.copyRate.startDate : this.state.todayDate
        }
        return this.state.todayDate
    }

    getAccountingUnitLabel(accountingUnit: AccountingUnitResponse): string {
        return `${accountingUnit.name} (${accountingUnit.symbol})`
    }

    onStartDate(value: Date): void {
        this.state.copyRate.startDate = value
        this._setDirtyStartDate()
    }

    onEndDate(value: Date): void {
        this.state.copyRate.endDate = value
        this._setDirtyEndDate()
    }

    onClearStartDate(): void {
        this.$nextTick(() => {
            this.state.copyRate.startDate = this.state.todayDate
            this._setDirtyStartDate(false)
        })
    }

    onClearEndDate(): void {
        this.state.copyRate.endDate = null
        this._setDirtyEndDate(false)
    }

    async show(rate: BillableRateResponse): Promise<void> {
        this.state = initialLocalAccountingRatesCopyModalState()
        this.state = {
            ...this.state,
            rateOriginalCopyName: rate.name,
            laboratoryId: this.$store.getters.labId,
            billableRateToBeCopiedId: rate.id,
            unit: rate.accountingUnit
        }
        await this._loadLaboratories()

        this.ratesCopyModal.show()
    }

    hide(): void {
        this.ratesCopyModal.hide()
    }

    private async _getUnits(): Promise<AccountingUnitResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }
        const response = await this.$_toCallSecure(configurationAccountingBillableUnitsService.paginator(filterModel, params))
        return response ? response.content : []
    }

    private async _loadLaboratories(): Promise<void> {
        const response =
            await this.$_toCallSecure(commonLaboratoryService.getAllActiveLaboratories())
        this.state.laboratories = response ?? []
        this.state.copyRate.laboratory =
            this.state.laboratories.find(laboratory => laboratory.labId === this.state.laboratoryId) ?? null
    }

    private _setDirtyStartDate(value = true): void {
        this.fields[this.$tc('configuration.grid.startDate')].dirty = value
    }

    private _setDirtyEndDate(value = true): void {
        this.fields[this.$tc('configuration.grid.endDate')].dirty = value
    }

    /// FORM OPERATIONS ///
    private _copyOperation(): Promise<StrictNullable<BillableRateResponse>> {
        return this._copyRate(
            {
                billableRateToBeCopiedId: this.state.billableRateToBeCopiedId,
                name: this.state.copyRate.name,
                code: this.state.copyRate.code,
                laboratoryId: this.state.copyRate.laboratory?.labId!,
                startDate: DateUtils.formatDate(this.state.copyRate.startDate)!,
                endDate: DateUtils.formatDate(this.state.copyRate.endDate)!,
                isCopyValues: this.state.copyRate.copyValues,
                isDefault: this.state.copyRate.isDefault,
                allowsCopayment: this.state.copyRate.allowsCopayment,
                accountingUnitId: this.state.unit.id
            }
        )
    }

    private async _copyRate(
        request: BillableRateCopyRequest
    ): Promise<StrictNullable<BillableRateResponse>> {
        if (request) {
            const response =
                await this.$_toCallSecure(
                    billableRateService.copy(this.state.laboratoryId!, request))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                return response
            }
        }
        return null
    }

    async doAction(): Promise<void> {
        if (await this._copyOperation()) {
            this.create() // emits an event
            await this.$validator.reset()
            this.hide()
        }
    }
}
